<template>
    <div class="quotes-table-wrapper">
        <TableScrollHeader
            :can-scroll-left="canScrollLeft"
            :can-scroll-right="canScrollRight"
            :scroll-percent="scrollPercent"
            @scroll-left="scrollHorizontally(-1)"
            @scroll-right="scrollHorizontally(1)"
        />
        <div class="table-scroll-container" ref="tableScroll" @scroll="updateScrollState">
            <table class="table table-striped table-class hidden-sm hidden-xs">
                <thead>
                    <tr>
                        <th class="select-header">
                            <div class="form-check">
                                <input
                                    class="form-check-input"
                                    type="checkbox"
                                    v-model="selectAll"
                                    @change="toggleSelectAll"
                                />
                            </div>
                        </th>
                        <th
                            v-for="(column, idx) in displayColumns"
                            :key="column.field || idx"
                            class="sortable-header"
                            @click="sortBy(column)"
                        >
                            <span class="header-label">{{ column.label }}</span>
                            <div v-if="idx === 0" class="header-research-wrapper">
                                <button
                                    type="button"
                                    class="link-button header-icon-button"
                                    @click.stop="sortByType"
                                    :title="'Sort by type'"
                                >
                                    <font-awesome-icon
                                        :icon="['fas', 'flask']"
                                        class="header-research-icon"
                                    />
                                </button>
                                <span
                                    v-if="sortField === 'quote_type'"
                                    class="research-sort-indicator"
                                >
                                    <font-awesome-icon
                                        v-if="sortDirection !== 'asc'"
                                        :icon="['fas', 'chevron-up']"
                                    />
                                    <font-awesome-icon
                                        v-if="sortDirection !== 'desc'"
                                        :icon="['fas', 'chevron-down']"
                                    />
                                </span>
                            </div>
                            <span v-if="sortField === column.field" class="sort-indicator">
                                <font-awesome-icon
                                    v-if="sortDirection !== 'asc'"
                                    :icon="['fas', 'chevron-up']"
                                />
                                <font-awesome-icon
                                    v-if="sortDirection !== 'desc'"
                                    :icon="['fas', 'chevron-down']"
                                />
                            </span>
                        </th>
                    </tr>
                </thead>
                <tbody v-if="paginatedData.length > 0">
                    <tr v-for="(row, rowIndex) in paginatedData" :key="rowIndex">
                        <td>
                            <div class="form-check">
                                <input
                                    class="form-check-input"
                                    type="checkbox"
                                    :value="row.id || rowIndex"
                                    v-model="selectedIds"
                                    @change="emitSelection"
                                />
                            </div>
                        </td>
                        <td v-for="(column, colIndex) in displayColumns" :key="colIndex">
                            <button
                                v-if="colIndex === 0"
                                type="button"
                                class="link-button"
                                @click="openDetails(row)"
                            >
                                <font-awesome-icon
                                    v-if="isResearchQuote(row)"
                                    :icon="['fas', 'flask']"
                                    class="research-icon"
                                />
                                {{ formatValue(row[column.field], column) }}
                            </button>
                            <span v-else>{{ formatValue(row[column.field], column) }}</span>
                        </td>
                    </tr>
                </tbody>
                <tbody v-else>
                    <tr>
                        <td colspan="100%" class="text-center" style="padding: 20px">
                            No data available
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div class="d-flex justify-content-between align-items-center my-3">
            <div>
                <label for="itemsPerPage" class="me-2">Items per page:</label>
                <select
                    id="itemsPerPage"
                    v-model.number="itemsPerPage"
                    @change="handleItemsPerPageChange"
                    class="form-select d-inline-block w-auto"
                >
                    <option v-for="option in [10, 20, 50, 100]" :key="option" :value="option">
                        {{ option }}
                    </option>
                </select>
            </div>

            <div>
                <button
                    class="btn btn-danger"
                    @click="changePage(currentPage - 1)"
                    :disabled="currentPage === 1 || totalPages === 0"
                >
                    Previous
                </button>
                <span class="mx-3">
                    Page <strong>{{ Math.min(currentPage, Math.max(totalPages, 1)) }}</strong> of
                    <strong>{{ Math.max(totalPages, 1) }}</strong>
                </span>
                <button
                    class="btn btn-danger"
                    @click="changePage(currentPage + 1)"
                    :disabled="currentPage === totalPages || totalPages === 0"
                >
                    Next
                </button>
            </div>
        </div>

        <transition name="details-slide">
            <aside
                v-if="detailsVisible"
                class="details-sidebar"
                role="dialog"
                aria-modal="true"
                ref="detailsSidebar"
                tabindex="-1"
                @click.stop
                @keyup.esc="closeDetails"
            >
                <div class="details-sidebar__header">
                    <div>
                        <p class="details-sidebar__eyebrow">Quote ID</p>
                        <h3 class="details-sidebar__title">{{ selectedRowId }}</h3>
                        <div v-if="selectedRow" class="details-sidebar__badges">
                            <span
                                class="status-pill"
                                :class="
                                    isResearchQuote(selectedRow)
                                        ? 'status-pill--info'
                                        : 'status-pill--neutral'
                                "
                            >
                                <span class="status-pill__icon">
                                    <font-awesome-icon
                                        :icon="[
                                            'fas',
                                            isResearchQuote(selectedRow)
                                                ? 'flask'
                                                : 'clipboard-list',
                                        ]"
                                    />
                                </span>
                                {{
                                    isResearchQuote(selectedRow)
                                        ? 'Research Quote'
                                        : 'Standard Quote'
                                }}
                            </span>
                        </div>
                    </div>
                    <button type="button" class="details-sidebar__close" @click="closeDetails">
                        <span aria-hidden="true">&times;</span>
                        <span class="visually-hidden">Close</span>
                    </button>
                </div>
                <div class="details-sidebar__content">
                    <dl>
                        <div v-for="column in columns" :key="column.field" class="detail-row">
                            <dt>{{ column.label }}</dt>
                            <dd>{{ formatDetailValue(column.field) }}</dd>
                        </div>
                    </dl>
                </div>
            </aside>
        </transition>
        <transition name="details-fade">
            <div v-if="detailsVisible" class="details-backdrop" @click="closeDetails"></div>
        </transition>
    </div>
</template>

<script>
    import TableScrollHeader from '@/components/shared/TableScrollHeader.vue'

    export default {
        name: 'DashboardQuotesTable',
        components: {
            TableScrollHeader,
        },
        props: {
            columns: {
                type: Array,
                required: true,
            },
            data: {
                type: Array,
                required: true,
            },
            initialSort: {
                type: Object,
                default: () => ({
                    field: '',
                    direction: 'desc',
                }),
            },
        },
        data() {
            return {
                sortField:
                    this.initialSort?.field ||
                    (this.columns || []).find((col) => col.field !== 'quote_type')?.field ||
                    '',
                sortDirection: this.initialSort?.direction || 'asc',
                currentPage: 1,
                itemsPerPage: 10,
                selectedIds: [],
                detailsVisible: false,
                selectedRow: null,
                scrollPercent: 0,
                canScrollLeft: false,
                canScrollRight: false,
                scrollStep: 240,
            }
        },
        computed: {
            displayColumns() {
                return (this.columns || []).filter((col) => col.field !== 'quote_type')
            },
            selectAll: {
                get() {
                    return (
                        this.selectedIds.length > 0 && this.selectedIds.length === this.data.length
                    )
                },
                set(value) {
                    if (value) {
                        this.selectedIds = this.data.map((item, idx) => item.id ?? idx)
                    } else {
                        this.selectedIds = []
                    }
                    this.emitSelection()
                },
            },
            sortedData() {
                if (!this.sortField) return this.data
                return this.data.slice().sort((a, b) => {
                    const column = this.columns.find((col) => col.field === this.sortField) || {}
                    const valueA = this.normalizeValue(a[this.sortField], column)
                    const valueB = this.normalizeValue(b[this.sortField], column)
                    if (valueA < valueB) return this.sortDirection === 'asc' ? -1 : 1
                    if (valueA > valueB) return this.sortDirection === 'asc' ? 1 : -1
                    return 0
                })
            },
            paginatedData() {
                const start = (this.currentPage - 1) * this.itemsPerPage
                return this.sortedData.slice(start, start + this.itemsPerPage)
            },
            totalPages() {
                return Math.ceil(this.data.length / this.itemsPerPage)
            },
            selectedRowId() {
                if (!this.selectedRow) return '—'
                return this.selectedRow.id ?? '—'
            },
        },
        watch: {
            data() {
                this.currentPage = 1
                this.resetScrollPosition()
            },
            columns() {
                if (!this.sortField && this.displayColumns.length > 0) {
                    this.sortField = this.displayColumns[0].field
                }
            },
        },
        mounted() {
            this.$nextTick(() => {
                this.updateScrollState()
            })
            window.addEventListener('resize', this.updateScrollState)
        },
        beforeUnmount() {
            window.removeEventListener('resize', this.updateScrollState)
        },
        methods: {
            sortBy(column) {
                if (this.sortField === column.field) {
                    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
                } else {
                    this.sortField = column.field
                    this.sortDirection = 'asc'
                }
            },
            sortByType() {
                if (this.sortField === 'quote_type') {
                    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
                } else {
                    this.sortField = 'quote_type'
                    this.sortDirection = 'asc'
                }
            },
            changePage(page) {
                if (page > 0 && page <= this.totalPages) {
                    this.currentPage = page
                }
            },
            handleItemsPerPageChange() {
                this.currentPage = 1
            },
            formatValue(value, column) {
                if (column?.formatFn && typeof column.formatFn === 'function') {
                    try {
                        return column.formatFn(value)
                    } catch (error) {
                        console.error('Failed to format value', error)
                        return value
                    }
                }
                return value
            },
            normalizeValue(value, column = {}) {
                if (column.type === 'number') {
                    const numeric = Number(
                        typeof value === 'string' ? value.replace(/[^0-9.-]+/g, '') : value
                    )
                    return isNaN(numeric) ? 0 : numeric
                }
                if (value instanceof Date) {
                    return value.getTime()
                }
                if (typeof value === 'string') {
                    const date = new Date(value)
                    if (!isNaN(date)) return date.getTime()
                    return value.toLowerCase()
                }
                return value ?? ''
            },
            toggleSelectAll() {
                this.selectAll = !this.selectAll
            },
            emitSelection() {
                this.$emit('selection-changed', this.selectedIds)
            },
            isResearchQuote(row) {
                const type = row?.quote_type
                if (!type) return false
                return String(type).toLowerCase().includes('research')
            },
            openDetails(row) {
                this.selectedRow = row
                this.detailsVisible = true
                this.$nextTick(() => {
                    this.$refs.detailsSidebar?.focus()
                })
            },
            closeDetails() {
                this.detailsVisible = false
                this.selectedRow = null
            },
            formatDetailValue(fieldKey) {
                if (!this.selectedRow) {
                    return '—'
                }
                const value = this.selectedRow[fieldKey]
                if (value === null || value === undefined || value === '') {
                    return '—'
                }
                if (Array.isArray(value)) {
                    return value.join(', ')
                }
                if (typeof value === 'boolean') {
                    return value ? 'Yes' : 'No'
                }
                if (typeof value === 'object') {
                    try {
                        return JSON.stringify(value, null, 2)
                    } catch (error) {
                        return String(value)
                    }
                }
                return value
            },
            scrollHorizontally(direction = 1) {
                const container = this.$refs.tableScroll
                if (!container) return
                const delta = this.scrollStep * direction
                container.scrollBy({ left: delta, behavior: 'smooth' })
            },
            updateScrollState() {
                const container = this.$refs.tableScroll
                if (!container) return
                const maxScroll = container.scrollWidth - container.clientWidth
                const current = container.scrollLeft
                this.canScrollLeft = current > 1
                this.canScrollRight = current < maxScroll - 1
                this.scrollPercent = maxScroll > 0 ? (current / maxScroll) * 100 : 0
            },
            resetScrollPosition() {
                this.$nextTick(() => {
                    const container = this.$refs.tableScroll
                    if (!container) return
                    container.scrollLeft = 0
                    this.updateScrollState()
                })
            },
        },
    }
</script>

<style scoped lang="scss">
    table th,
    table td {
        text-align: center;
    }

    table tr:nth-child(even) {
        background-color: #bef2f5;
    }

    .table {
        thead {
            td,
            th {
                color: #ffffff;
                background-color: #000 !important;
            }
        }
    }

    .table-scroll-container {
        overflow-x: auto;
        border-radius: 12px;
        border: 1px solid #e5e7eb;
        box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.04);
        padding-bottom: 6px;
        background: #ffffff;
        position: relative;
    }

    .sortable-header {
        cursor: pointer;
    }

    .sort-indicator {
        margin-left: 6px;
    }

    .research-icon {
        margin-right: 6px;
        color: #1e40af;
    }

    .header-research-icon {
        margin-right: 0;
        color: #ffffff;
    }

    .link-button {
        display: flex;
        background: none;
        border: none;
        padding: 0;
        color: #dd1864;
        font-weight: 600;
        cursor: pointer;
    }
    .header-icon-button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        margin-right: 6px;
    }
    .header-label {
        display: block;
    }
    .header-research-wrapper {
        display: flex;
        justify-content: center;
        margin-top: 4px;
    }
    .research-sort-indicator {
        margin-left: 6px;
        color: #ffffff;
    }

    .details-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(15, 23, 42, 0.45);
        z-index: 1040;
    }

    .details-sidebar {
        position: fixed;
        top: 0;
        right: 0;
        width: 380px;
        max-width: 90vw;
        height: 100vh;
        background: #fff;
        box-shadow: -12px 0 30px rgba(15, 23, 42, 0.18);
        z-index: 1050;
        padding: 1.5rem;
        display: flex;
        flex-direction: column;
        overflow-y: auto;
    }

    .details-sidebar__header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 1.5rem;
        gap: 1rem;
    }

    .details-sidebar__eyebrow {
        font-size: 0.75rem;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        margin: 0 0 0.25rem;
        color: #6b7280;
    }

    .details-sidebar__title {
        margin: 0;
        font-size: 1.75rem;
        font-weight: 600;
    }

    .details-sidebar__close {
        border: none;
        background: none;
        font-size: 1.75rem;
        line-height: 1;
        cursor: pointer;
        padding: 0;
        color: #374151;
    }

    .details-sidebar__content {
        flex: 1;
    }

    .details-sidebar__badges {
        display: flex;
        flex-wrap: wrap;
        gap: 0.4rem;
        margin-top: 0.75rem;
    }

    .status-pill {
        display: inline-flex;
        align-items: center;
        gap: 0.35rem;
        padding: 0.15rem 0.65rem;
        border-radius: 999px;
        font-size: 0.75rem;
        font-weight: 600;
        letter-spacing: 0.02em;
        color: #0f172a;
        background: #e2e8f0;
        position: relative;
        overflow: hidden;
    }

    .status-pill__icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 0.85rem;
        position: relative;
    }

    .status-pill--info {
        color: #0b4b8a;
        background: rgba(59, 130, 246, 0.15);
    }

    .status-pill--neutral {
        color: #475569;
        background: rgba(148, 163, 184, 0.35);
    }

    .detail-row {
        margin-bottom: 1.25rem;
        padding-bottom: 1.25rem;
        border-bottom: 1px solid #f1f5f9;

        &:last-child {
            border-bottom: none;
            padding-bottom: 0;
        }
    }

    .detail-row dt {
        font-size: 0.75rem;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        margin: 0 0 0.25rem;
        color: #6b7280;
    }

    .detail-row dd {
        margin: 0;
        font-weight: 500;
        color: #111827;
        white-space: pre-wrap;
        word-break: break-word;
    }

    .details-slide-enter-active,
    .details-slide-leave-active {
        transition:
            transform 0.3s ease,
            opacity 0.3s ease;
    }

    .details-slide-enter-from,
    .details-slide-leave-to {
        transform: translateX(100%);
        opacity: 0;
    }

    .details-fade-enter-active,
    .details-fade-leave-active {
        transition: opacity 0.2s ease;
    }

    .details-fade-enter-from,
    .details-fade-leave-to {
        opacity: 0;
    }
</style>
